import { debounce } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import type { AgentRunsFilters as Filters } from '~/entities/document-agent'
import { DatePicker, Segment, SegmentedButton, Textfield } from '~/shared/ui'
import { RUN_LABEL_DEBOUNCE_MS, RUN_LABEL_PRESETS } from '../config'
import { calendarDateToDate, dateToCalendarDate } from '../lib'
import type { AgentRunsFiltersUpdate } from '../model'

export function AgentRunsFilters({
  filters,
  onChange,
}: {
  filters: Filters
  onChange: (next: AgentRunsFiltersUpdate) => void
}) {
  // Stable Date objects: the picker resets what the user is typing whenever `date` changes.
  const dateFrom = useMemo(() => calendarDateToDate(filters.dateFrom), [filters.dateFrom])
  const dateTo = useMemo(() => calendarDateToDate(filters.dateTo), [filters.dateTo])
  const [labelDraft, setLabelDraft] = useState(filters.runLabel)
  const commitLabel = useMemo(
    () => debounce((value: string) => onChange({ runLabel: value.trim() }), RUN_LABEL_DEBOUNCE_MS),
    [onChange],
  )

  useEffect(() => () => commitLabel.cancel(), [commitLabel])

  useEffect(() => {
    setLabelDraft(filters.runLabel)
  }, [filters.runLabel])

  const handleLabelChange = (value: string) => {
    setLabelDraft(value)
    commitLabel(value)
  }

  const handlePresetClick = (value: string) => {
    commitLabel.cancel()
    setLabelDraft(value)
    onChange({ runLabel: value })
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <DatePicker
        className="w-[190px]"
        label="Document date from"
        date={dateFrom}
        maxDate={dateTo}
        onDateChange={(date) => onChange({ dateFrom: date ? dateToCalendarDate(date) : null })}
      />
      <DatePicker
        className="w-[190px]"
        label="Document date to"
        date={dateTo}
        minDate={dateFrom}
        onDateChange={(date) => onChange({ dateTo: date ? dateToCalendarDate(date) : null })}
      />
      <SegmentedButton size="small">
        {RUN_LABEL_PRESETS.map((preset) => (
          <Segment
            key={preset.label}
            checked={filters.runLabel === preset.value}
            onClick={() => handlePresetClick(preset.value)}
          >
            {preset.label}
          </Segment>
        ))}
      </SegmentedButton>
      <Textfield
        className="w-[220px]"
        label="Run label prefix"
        placeholder="e.g. replay:gold"
        value={labelDraft}
        clearable={labelDraft.length > 0}
        materialClearIcon="close"
        onClear={() => handlePresetClick('')}
        onChange={(event) => handleLabelChange(event.target.value)}
      />
    </div>
  )
}
